import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Boxes, ScanSearch, ShieldAlert, History, Rocket, Activity, PackageSearch, Loader2, GitFork, ListChecks, FileCode } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe, PILLARS } from '@/lib/luxe';
import { GlassCard, LuxeButton, Input, Spinner } from '@/components/bits';

const P = PILLARS.coding;

const TABS = [
  ['discover', 'Explorer', Boxes],
  ['tasks', 'Tasks', ListChecks],
  ['files', 'Files', FileCode],
  ['deps', 'Dependencies', PackageSearch],
  ['security', 'Security', ShieldAlert],
  ['snapshots', 'Snapshots', History],
  ['deploy', 'Deploy', Rocket],
  ['readiness', 'Readiness', Activity],
];

const TASK_TONE = {
  QUEUED: luxe.muted, RUNNING: luxe.blue, VERIFYING: luxe.blue, RECOVERING: luxe.warning,
  WAITING_APPROVAL: luxe.warning, COMPLETED: luxe.success, FAILED: luxe.danger, CANCELLED: luxe.muted,
};

const Badge = ({ text, tone = luxe.muted }) => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: luxe.surface2, color: tone, border: `1px solid ${luxe.border}` }}>{text}</span>
);

const STATE_TONE = {
  SAFE: luxe.success, CLEAN: luxe.success, implemented: luxe.success, configured: luxe.success,
  WARNINGS: luxe.warning, REVIEW: luxe.warning, environment_limitation: luxe.warning, not_configured: luxe.muted,
  VULNERABILITIES_FOUND: luxe.danger, MALICIOUS_SUSPECTED: luxe.danger, SUSPICIOUS: luxe.danger, missing: luxe.danger,
  SCAN_UNAVAILABLE: luxe.muted,
};

// Additive read/ops console over the Phase 4-15 backend. Does NOT replace Myra Build.
export const CodingConsole = ({ projects }) => {
  const buildProjects = (projects || []).filter((p) => p.id !== 'cambri');
  const [project, setProject] = useState(buildProjects[0]?.id || 'cambri');
  const [tab, setTab] = useState('discover');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({});
  const [snapLabel, setSnapLabel] = useState('');
  const [fileSel, setFileSel] = useState(null);
  const [taskStep, setTaskStep] = useState('');

  useEffect(() => { if (!project && buildProjects[0]) setProject(buildProjects[0].id); }, [projects]); // eslint-disable-line

  const run = async (key, fn) => {
    setBusy(true);
    try { const r = await fn(); setData((d) => ({ ...d, [key]: r })); }
    catch (e) { toast.error((e && e.message) || 'Request failed'); }
    finally { setBusy(false); }
  };

  const load = () => {
    if (tab === 'discover') run('discover', () => api('/admin/coding/project/discover', { method: 'POST', body: { project } }));
    if (tab === 'tasks') run('tasks', () => api(`/admin/coding/queue?project=${encodeURIComponent(project)}`));
    if (tab === 'files') run('files', () => api(`/admin/agents/projects/${project}/files`));
    if (tab === 'deps') run('deps', () => api('/admin/coding/dependencies/scan', { method: 'POST', body: { project } }));
    if (tab === 'security') run('security', () => api('/admin/coding/malware/scan', { method: 'POST', body: { project } }));
    if (tab === 'snapshots') run('snapshots', () => api(`/admin/coding/vcs/snapshots/${project}`));
    if (tab === 'deploy') run('deploy', async () => ({ adapters: (await api('/admin/coding/deploy/adapters')).adapters, precheck: await api('/admin/coding/deploy/precheck', { method: 'POST', body: { project } }) }));
    if (tab === 'readiness') run('readiness', () => api('/admin/coding/readiness'));
  };
  useEffect(() => { load(); }, [tab, project]); // eslint-disable-line

  const reloadTasks = () => run('tasks', () => api(`/admin/coding/queue?project=${encodeURIComponent(project)}`));

  const createTask = async () => {
    if (!taskStep.trim()) return toast.error('Describe the step/goal for the task');
    try {
      await api('/admin/coding/queue', { method: 'POST', body: { project, agent: 'coding_agent', step: taskStep.trim(), requires_approval: true } });
      toast.success('Task queued (awaiting approval)'); setTaskStep(''); reloadTasks();
    } catch (e) { toast.error((e && e.message) || 'Could not queue task'); }
  };

  const taskAction = async (id, action) => {
    try { await api(`/admin/coding/queue/${id}/${action}`, { method: 'POST' }); toast.success(action); reloadTasks(); }
    catch (e) { toast.error((e && e.message) || 'Action failed'); }
  };

  const openFile = async (path) => {
    try { const r = await api(`/admin/agents/projects/${project}/file?path=${encodeURIComponent(path)}`); setFileSel(r); }
    catch (e) { toast.error((e && e.message) || 'Cannot open file'); }
  };

  const createSnapshot = async () => {
    if (!snapLabel.trim()) return toast.error('Enter a snapshot label');
    await run('snapshots', async () => { await api('/admin/coding/vcs/snapshot', { method: 'POST', body: { project, label: snapLabel.trim() } }); setSnapLabel(''); return api(`/admin/coding/vcs/snapshots/${project}`); });
    toast.success('Snapshot created');
  };

  const forkProject = async () => {
    const name = window.prompt('Fork name (new independent copy — secrets are NOT copied):');
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      const r = await api('/admin/agents/projects/fork', { method: 'POST', body: { source_project: project, name: name.trim() } });
      toast.success(`Forked → ${r.id} (secrets not copied, ${r.memory_entries_copied} memories)`);
    } catch (e) { toast.error((e && e.message) || 'Fork failed'); }
    finally { setBusy(false); }
  };

  const d = data[tab];

  return (
    <GlassCard className="p-6 mb-8" data-testid="coding-console">
      <div className="flex items-center gap-2 mb-1">
        <ScanSearch size={18} style={{ color: P.primary }} />
        <span className="font-bold text-lg" style={{ color: luxe.text }}>Project Console</span>
        <Badge text="secure ops · read + snapshots + deploy checks" />
      </div>
      <p className="text-sm mb-4" style={{ color: luxe.muted }}>Understand, secure and ship a project — sandboxed scans, dependency & malware checks, snapshots, deploy prechecks and production readiness.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs" style={{ color: luxe.muted }}>Project:</span>
        <select value={project} onChange={(e) => setProject(e.target.value)} data-testid="console-project-select"
          className="text-sm rounded-lg px-3 py-2" style={{ background: luxe.surface2, color: luxe.text, border: `1px solid ${luxe.border}` }}>
          <option value="cambri">Cambri (live)</option>
          {buildProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {busy && <Loader2 size={15} className="animate-spin" style={{ color: luxe.muted }} />}
        <button onClick={forkProject} data-testid="console-fork-btn"
          className="ml-auto px-3 py-2 rounded-full text-sm font-bold luxe-btn"
          style={{ background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
          <GitFork size={14} className="inline mr-1" />Fork Project
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`console-tab-${id}`}
            className="px-3 py-2 rounded-full text-sm font-bold luxe-btn"
            style={tab === id ? { background: P.primary, color: '#fff' } : { background: luxe.surface2, color: luxe.textMid, border: `1px solid ${luxe.border}` }}>
            <Icon size={14} className="inline mr-1" />{label}
          </button>
        ))}
      </div>

      <div data-testid="console-body" className="text-sm" style={{ color: luxe.textMid }}>
        {!d && <div className="flex items-center gap-2" style={{ color: luxe.muted }}><Spinner /> Loading…</div>}

        {tab === 'tasks' && d && (
          <div data-testid="console-tasks">
            <div className="flex gap-2 mb-3">
              <Input value={taskStep} onChange={(e) => setTaskStep(e.target.value)} placeholder="New task goal (queued, awaiting approval)…" data-testid="console-task-step" />
              <LuxeButton variant="pillar" color={P} onClick={createTask} data-testid="console-task-create">Queue</LuxeButton>
            </div>
            {(d.tasks || []).length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>No tasks yet for this project.</div> : (
              <div className="space-y-2">{d.tasks.map((t) => (
                <div key={t.id} className="p-3 rounded-lg text-xs" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }} data-testid={`task-row-${t.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-bold truncate" style={{ color: luxe.text }}>{t.current_step || t.assigned_agent}</span>
                    <Badge text={t.status} tone={TASK_TONE[t.status] || luxe.muted} />
                  </div>
                  <div style={{ color: luxe.muted }}>agent {t.assigned_agent} · attempt {t.attempt_count}/{t.max_attempts} · {(t.updated_at || '').slice(0, 16)}</div>
                  {t.failure && <div className="mt-1" style={{ color: luxe.danger }}>failure: {t.failure.category} — {String(t.failure.error || '').slice(0, 160)}</div>}
                  <div className="flex gap-2 mt-2">
                    {t.status === 'WAITING_APPROVAL' && <button onClick={() => taskAction(t.id, 'approve')} data-testid={`task-approve-${t.id}`} className="px-2 py-1 rounded-lg font-bold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.success }}>Approve</button>}
                    {t.status === 'FAILED' && <button onClick={() => taskAction(t.id, 'retry')} data-testid={`task-retry-${t.id}`} className="px-2 py-1 rounded-lg font-bold" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.warning }}>Retry</button>}
                    {['QUEUED', 'WAITING_APPROVAL', 'RUNNING'].includes(t.status) && <button onClick={() => taskAction(t.id, 'cancel')} data-testid={`task-cancel-${t.id}`} className="px-2 py-1 rounded-lg font-bold" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#FCA5A5' }}>Cancel</button>}
                  </div>
                </div>
              ))}</div>
            )}
          </div>
        )}

        {tab === 'files' && d && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="console-files">
            <div className="rounded-lg p-2" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, maxHeight: 360, overflow: 'auto' }}>
              {(d.items || []).length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>No files.</div>
                : d.items.slice(0, 300).map((f) => (
                  <button key={f.path} onClick={() => openFile(f.path)} data-testid={`file-item-${f.path}`} className="block w-full text-left px-2 py-1 rounded text-xs font-mono truncate hover:opacity-80" style={{ color: fileSel?.path === f.path ? luxe.text : luxe.textMid }}>{f.path}</button>
                ))}
            </div>
            <div className="rounded-lg p-2" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}`, maxHeight: 360, overflow: 'auto' }}>
              {fileSel ? <pre className="text-[11px] font-mono whitespace-pre-wrap" style={{ color: luxe.text }} data-testid="file-content">{fileSel.content}</pre>
                : <div className="text-xs" style={{ color: luxe.muted }}>Select a file to view.</div>}
            </div>
          </div>
        )}

        {tab === 'discover' && d && (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="console-discover">
            {[['Languages', (d.languages || []).join(', ')], ['Frameworks', (d.frameworks || []).join(', ')], ['Backend', d.backend], ['Database', d.database], ['Files', d.file_count], ['Entry points', (d.entry_points || []).join(', ')]].map(([k, v]) => (
              <div key={k} className="p-3 rounded-lg" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: luxe.muted }}>{k}</div>
                <div style={{ color: luxe.text }}>{v || '—'}</div>
              </div>
            ))}
            {(d.api_routes || []).length > 0 && <div className="sm:col-span-2 p-3 rounded-lg" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}><div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>API routes ({d.api_routes.length})</div><div className="font-mono text-xs" style={{ color: luxe.text, maxHeight: 120, overflow: 'auto' }}>{d.api_routes.slice(0, 30).join('  ·  ')}</div></div>}
          </div>
        )}

        {tab === 'deps' && d && (
          <div data-testid="console-deps">
            <div className="mb-2">Scan state: <Badge text={d.state} tone={STATE_TONE[d.state]} /></div>
            {Object.entries(d.inventory || {}).map(([f, info]) => <div key={f} className="text-xs mb-1"><b style={{ color: luxe.text }}>{f}</b>: {info.count} deps</div>)}
            {(d.findings || []).length > 0 ? <ul className="mt-2 list-disc pl-4 text-xs">{d.findings.slice(0, 20).map((f, i) => <li key={i}>{f.package} — {f.severity} ({f.ecosystem})</li>)}</ul> : <div className="text-xs mt-1" style={{ color: luxe.muted }}>No vulnerabilities reported. Scanners run: {(d.scanners_ran || []).join(', ') || 'none'} · unavailable: {(d.scanners_unavailable || []).join(', ') || 'none'}</div>}
          </div>
        )}

        {tab === 'security' && d && (
          <div data-testid="console-security">
            <div className="mb-2">Verdict: <Badge text={d.verdict} tone={STATE_TONE[d.verdict]} /> · files scanned: {d.files_scanned}</div>
            <div className="flex gap-2 mb-2 text-xs">{Object.entries(d.counts || {}).map(([k, v]) => <span key={k} style={{ color: v ? luxe.warning : luxe.muted }}>{k}: {v}</span>)}</div>
            {(d.findings || []).length > 0 ? <ul className="list-disc pl-4 text-xs">{d.findings.slice(0, 15).map((f, i) => <li key={i}><b>{f.category}</b> ({f.severity}) — {f.source}:{f.line}</li>)}</ul> : <div className="text-xs" style={{ color: luxe.success }}>No suspicious patterns found.</div>}
            <div className="text-[10px] mt-2" style={{ color: luxe.muted }}>{d.note}</div>
          </div>
        )}

        {tab === 'snapshots' && d && (
          <div data-testid="console-snapshots">
            <div className="flex gap-2 mb-3">
              <Input value={snapLabel} onChange={(e) => setSnapLabel(e.target.value)} placeholder="Snapshot label…" data-testid="console-snapshot-label" />
              <LuxeButton variant="pillar" color={P} onClick={createSnapshot} data-testid="console-snapshot-create">Snapshot</LuxeButton>
            </div>
            {(d.snapshots || []).length === 0 ? <div className="text-xs" style={{ color: luxe.muted }}>No snapshots yet.</div> : (
              <div className="space-y-1">{d.snapshots.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg text-xs" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
                  <span style={{ color: luxe.text }}>{s.label}</span>
                  <span className="font-mono" style={{ color: luxe.muted }}>{s.sha} · {(s.at || '').slice(0, 16)}</span>
                </div>
              ))}</div>
            )}
          </div>
        )}

        {tab === 'deploy' && d && (
          <div data-testid="console-deploy">
            <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>Adapters</div>
            <div className="flex flex-wrap gap-2 mb-3">{(d.adapters || []).map((a) => <span key={a.id} className="text-xs px-2 py-1 rounded-lg" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>{a.name} <Badge text={a.status} tone={STATE_TONE[a.status]} /></span>)}</div>
            <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: luxe.muted }}>Deploy precheck</div>
            <div className="mb-2">Can deploy: <b style={{ color: d.precheck?.can_deploy ? luxe.success : luxe.danger }}>{String(d.precheck?.can_deploy)}</b></div>
            {Object.entries(d.precheck?.checks || {}).map(([k, v]) => <div key={k} className="text-xs">{v.ok === false ? '🔴' : '🟢'} {k} {v.state ? `· ${v.state}` : ''}{v.verdict ? `· ${v.verdict}` : ''}</div>)}
          </div>
        )}

        {tab === 'readiness' && d && (
          <div data-testid="console-readiness">
            <div className="flex gap-3 mb-3 text-xs">
              <span style={{ color: luxe.success }}>implemented: {d.summary?.implemented}</span>
              <span style={{ color: luxe.warning }}>env-limitation: {d.summary?.environment_limitation}</span>
              <span style={{ color: luxe.muted }}>not-configured: {d.summary?.not_configured}</span>
              <span style={{ color: luxe.danger }}>missing: {d.summary?.missing}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-1">
              {Object.entries(d.matrix || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between p-2 rounded-lg text-xs" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
                  <span style={{ color: luxe.text }}>{k}</span><Badge text={v.status} tone={STATE_TONE[v.status]} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};
